import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { PageBackground } from '@/components/ui/page-background';
import { PageHero } from '@/components/ui/page-hero';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { SEO } from '@/components/seo';

const filters = ['All', 'Web App', 'SaaS'];

const cases = [
  {
    id: 'propertyagent-crm',
    title: 'PropertyAgent-CRM',
    client: 'Internal Product',
    category: 'Web App',
    industry: 'Real Estate / PropTech',
    challenge: 'Real estate agents struggled with generic CRMs not designed for their unique workflow - from lead capture to closing deals. They needed mobile access for field work and AI-powered insights.',
    result: 'Built a complete AI-powered CRM with visual Kanban pipeline, commission forecasting, and mobile-first PWA design specifically for real estate professionals.',
    metrics: ['+30% closing rate', 'Mobile-First PWA', 'AI Pipeline Analysis'],
    image: '/images/portfolio/propertyagent-dashboard.png',
    logo: '/images/portfolio/propertyagent-logo.png',
    tags: ['Web App Development', 'AI Integration', 'PWA'],
    link: 'https://propertyagent-crm.com'
  },
  {
    id: 'selgr',
    title: 'SELGR',
    client: 'Internal Product',
    category: 'SaaS',
    industry: 'Sales Training / EdTech',
    challenge: 'Norwegian B2B salespeople lacked practical, hands-on training tools. Traditional sales courses were theoretical and didn\'t offer realistic practice scenarios with instant feedback.',
    result: 'Built an AI-powered sales training platform with interactive course modules, quizzes, and realistic AI roleplay simulations that adapt to each user\'s product, company, and skill level.',
    metrics: ['AI Roleplay', 'Personalized Learning', '10+ Course Modules'],
    image: '/images/portfolio/selgr-roleplay.png',
    logo: '/images/portfolio/selgr-logo.png',
    tags: ['SaaS Development', 'AI Integration', 'EdTech'],
    link: 'https://selgr.no'
  }
];

const Portfolio = () => {
  const [selectedFilter, setSelectedFilter] = useState('All');
  const { elementRef, getRevealStyle } = useScrollReveal();

  const filteredCases = selectedFilter === 'All' 
    ? cases 
    : cases.filter(case_study => case_study.category === selectedFilter);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Portfolio",
    "description": "Web development case studies and success stories from AffinityAim",
    "url": "https://www.affinityaim.com/portfolio"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.affinityaim.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Portfolio",
        "item": "https://www.affinityaim.com/portfolio"
      }
    ]
  };

  return (
    <PageBackground variant="aurora">
      <SEO
        title="Portfolio - Web Development Case Studies & Results | AffinityAim"
        description="Explore our web development portfolio. Real case studies with measurable results: 63% conversion increases, 28% revenue growth, and more."
        canonical="https://www.affinityaim.com/portfolio"
        keywords="web development portfolio, case studies, client success stories, web design examples"
        schema={[collectionSchema, breadcrumbSchema]}
      />
      <Navigation />

      <main id="main-content">
        <PageHero
          label="Portfolio"
          title="Results that speak"
          titleAccent="for themselves"
          description="Real projects, measurable outcomes. See how we've helped companies grow through better digital experiences."
        />

        {/* Filters */}
        <section className="pb-12">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-wrap justify-center gap-3">
                {filters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setSelectedFilter(filter)}
                    className={`px-6 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background tap-target ${
                      selectedFilter === filter
                        ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20'
                        : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-surface/50'
                    }`}
                    aria-pressed={selectedFilter === filter}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies Grid */}
        <section className="pb-24">
          <div className="container mx-auto px-6">
            <div ref={elementRef} className="max-w-6xl mx-auto">
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {filteredCases.map((case_study, index) => (
                  <a 
                    key={case_study.id}
                    href={case_study.link || '/contact'}
                    target={case_study.link ? '_blank' : '_self'}
                    rel={case_study.link ? 'noopener noreferrer' : undefined}
                    className="group tap-target"
                    style={getRevealStyle(index)}
                    aria-label={case_study.link ? `Visit ${case_study.title} website` : `Contact us about ${case_study.title}`}
                  >
                    <div className="h-full rounded-2xl bg-surface/50 border border-border overflow-hidden hover:border-primary/30 transition-all duration-500 hover:shadow-xl hover:shadow-primary/10">
                      
                      {/* Image */}
                      <div className="aspect-video bg-background relative overflow-hidden">
                        <img 
                          src={case_study.image} 
                          alt={`${case_study.title} project preview`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          loading="lazy"
                          width="600"
                          height="400"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
                        {case_study.logo && (
                          <div className="absolute top-4 left-4 bg-white rounded-xl p-2.5 shadow-lg">
                            <img 
                              src={case_study.logo} 
                              alt={`${case_study.title} logo`}
                              className="h-6 w-auto"
                            />
                          </div>
                        )}
                        
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>

                      {/* Content */}
                      <div className="p-8">
                        
                        {/* Header */}
                        <div className="mb-5">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="px-3 py-1 text-xs font-semibold text-primary bg-primary/10 rounded-full border border-primary/20">
                              {case_study.category}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {case_study.industry}
                            </span>
                          </div>
                          <h3 className="font-display font-bold text-foreground text-2xl mb-2 group-hover:text-primary transition-colors duration-300">
                            {case_study.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {case_study.client}
                          </p>
                        </div>

                        {/* Challenge & Result */}
                        <div className="mb-6 space-y-3">
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            <span className="font-semibold text-foreground">Challenge:</span> {case_study.challenge}
                          </p>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            <span className="font-semibold text-foreground">Solution:</span> {case_study.result}
                          </p>
                        </div>

                        {/* Metrics */}
                        <div className="mb-6">
                          <div className="flex flex-wrap gap-2">
                            {case_study.metrics.map((metric, idx) => (
                              <span 
                                key={idx}
                                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                                  idx === 0 
                                    ? 'bg-primary/10 border border-primary/20 text-primary' 
                                    : 'bg-surface border border-border text-muted-foreground'
                                }`}
                              >
                                {metric}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Tags & CTA */}
                        <div className="flex items-center justify-between pt-5 border-t border-border/50">
                          <div className="flex flex-wrap gap-2">
                            {case_study.tags.slice(0, 2).map((tag, idx) => (
                              <span 
                                key={idx}
                                className="text-xs text-muted-foreground bg-background px-2.5 py-1 rounded-md"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                          {case_study.link ? (
                            <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all duration-300">
                              <span className="text-sm">View live</span>
                              <ExternalLink className="w-4 h-4" />
                            </div>
                          ) : (
                            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" />
                          )}
                        </div>

                      </div>
                    </div>
                  </a>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
          
          <div className="container mx-auto px-6 relative">
            <div className="max-w-3xl mx-auto text-center">
              
              <h2 className="font-display font-bold text-foreground mb-6" style={{ fontSize: 'clamp(32px, 5vw, 48px)', lineHeight: '1.1' }}>
                Ready to create your success story?
              </h2>
              <p className="text-muted-foreground mb-10 text-lg max-w-xl mx-auto">
                Let's discuss your project and see how we can help you achieve similar results.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-14 text-base">
                    Start your project
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" size="lg" className="h-14 px-8 text-base border-border hover:bg-surface hover:border-primary/30">
                    View services
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </PageBackground>
  );
};

export default Portfolio;
